import { ComponentProps } from 'preact';
import { ForwardedRef, forwardRef, useCallback } from 'preact/compat';

import { Floating } from '../UNSAFE_Floating';
import { Layer } from '../UNSAFE_Layer';

import { MenuContainer } from './MenuContainer';
import { BaseMenuProps } from './Menu';
import { keyElemExtractor } from './menuUtils';
import { useMenuContext } from './useMenuContext';
import { dropdownMenuStyles } from './themes/DropdownMenuStyles.css';

type DropdownMenuProps = BaseMenuProps & DropdownMenuFloatingProps;

type FloatingProps = ComponentProps<typeof Floating>;
type DropdownMenuFloatingProps = {
  defaultPlacement: FloatingProps['placement'];
  offsetValue: FloatingProps['offsetValue'];
  flipOptions: FloatingProps['flipOptions'];
};

/*Component used to render a dropdown menu. For now this is just rendered when device is not a phone. */
export const DropdownMenu = forwardRef(
  (
    {
      anchorRef,
      defaultPlacement,
      offsetValue,
      closeSubmenu,
      children,
      'aria-label': ariaLabel,
      currentFocus,
      menuId,
      menuLevel,
      flipOptions
    }: DropdownMenuProps,
    ref?: ForwardedRef<HTMLDivElement>
  ) => {
    const { onClose } = useMenuContext();

    const useOutsideClickHandler = useCallback(
      (e: MouseEvent) => {
        if (menuLevel === 'sub') return;
        // Outside click is not considered when menuItem was clicked
        const menuItemKey = keyElemExtractor(e.target as HTMLElement)?.key;
        if (!menuItemKey) {
          onClose?.({ reason: 'outsideClick', target: e.target as Element });
        }
      },
      [onClose, menuLevel]
    );

    // TODO - Reuse a dropdown component instead of creating floating by ourselves.
    return (
      <Layer logicalParentRef={anchorRef}>
        <Floating
          class={dropdownMenuStyles.base}
          onClickOutside={useOutsideClickHandler}
          anchorRef={anchorRef}
          flipOptions={flipOptions}
          shiftOptions={{
            mainAxis: false,
            crossAxis: false
          }}
          sizeOptions={{ isMaxHeightAdjusted: true, isMaxWidthAdjusted: true }}
          //TODO: JET-57950. Need to see how we set 24px boundary.
          //TODO: Boundaries need to match with the dropdowns used in other comps.  Also figure out how to make it themable.
          boundaryOptions={{ padding: 0 }}
          placement={defaultPlacement}
          offsetValue={offsetValue}>
          <div
            style={{
              minWidth: `${
                anchorRef.current && anchorRef.current.offsetWidth > MENU_MIN_WIDTH
                  ? anchorRef.current.offsetWidth
                  : MENU_MIN_WIDTH
              }px`
            }}
            ref={ref}>
            <MenuContainer
              menuLevel={menuLevel}
              closeSubmenu={closeSubmenu}
              aria-label={ariaLabel}
              currentFocus={currentFocus}
              menuId={menuId}
              display="dropdown">
              {children}
            </MenuContainer>
          </div>
        </Floating>
      </Layer>
    );
  }
);

const MENU_MIN_WIDTH = 200;
